/******************************** Import Package ************************************/
import React, { useState, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { CssBaseline, Divider, IconButton } from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import { ChevronRight, ChevronLeft } from "@mui/icons-material";

/******************************* Import Pages ************************************/
import Dashboard from "../Pages/Dashboard";
import Unauthorized from "../Pages/Unauthorized";
import Login from "../Pages/Login";
import TimeOut from "../Pages/TimeOut";
import GoogleAuth from "../Pages/GoogleAuth";

/******************************* Import Components *********************************/
import SideNavigate from "./SideNavigate";
import Header from "./Header";
import Loader from "../Components/Loader";
import Notification from "../Components/Notification";

/******************************* Import Constants **********************************/
import * as menuData from "../Constants/menuData";
import * as dashBoardData from "../Constants/dashboardData";
import * as accessLevel from "../Constants/accessLevel";
import { env, hostConfig, idleTime } from "../Config";

/******************************* Import Styles **********************************/
import { useAppStyles } from "./App.Style.js";
import "./App.css";

/******************************* Import Pages lazy load ************************************/
const UserProjects = React.lazy(() => import("../Pages/User/UserProjects"));
const MyProjects = React.lazy(() => import("../Pages/User/MyProjects"));
const MyRequests = React.lazy(() => import("../Pages/User/MyRequests"));
const PendingRequests = React.lazy(() =>
  import("../Pages/User/PendingRequests")
);
const NotFound = React.lazy(() => import("../Pages/NotFound"));


/*********************************** App ****************************************/
export default function App() {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  let roleData;
  let menuLinks;
  let dashBoardLinks;
  // let role;
  let userData = {};

  const {
    Root,
    AppBarStyled,
    MenuButton,
    DrawerStyled,
    ToolbarStyled,
    SideMenu,
    SideMenuItem,
    CustomWidth,
    Content,
  } = useAppStyles();

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  /*********************************** Find User Role ****************************************/
  if (JSON.parse(localStorage.getItem("loggedUser"))) {
    userData = JSON.parse(localStorage.getItem("loggedUser"));
    if (window.location.pathname === "/") {
      window.location.href = "/dashboard";
    }
  } else {
    const location = window.location.pathname;

    if (location !== "/login") {
      if (location === "/google/auth" || location === "/unauthorized") {
      } else {
        if (env === 4 || env === 2) {
          window.location.href = `${hostConfig.API_URL}auth/google`;
        } else {
          window.location.href = "/login";
        } 
      }
    }
  }

  /*********************************** Inactive Time ****************************************/
  const inactivityTime = () => {
    let time;
    const logout = () => {
      window.location.href = "/time-out";
    };
    const resetTimer = () => {
      clearTimeout(time);
      time = setTimeout(logout, idleTime);
    };
    window.onload = resetTimer;
    window.addEventListener("load", resetTimer, true);
    [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "touchmove",
      "wheel",
      "select",
    ].forEach((name) => {
      document.addEventListener(name, resetTimer, true);
    });
  };

  window.onload = () => {
    inactivityTime();
  };

  /*********************************** Response Handler ****************************************/
  const responseHandler = (res) => {
    if (res) {
      if (res.code) {
        if (res.code === 400) {
          setNotify({ isOpen: true, message: res.message, type: "error" });
          return false;
        } else {
          setNotify({ isOpen: true, message: "Bad Request", type: "error" });
          return false;
        }
      } else if (res.error) {
        setNotify({ isOpen: true, message: res.error, type: "error" });
        return false;
      } else {
        return res;
      }
    } else {
      return false;
    }
  };

  /*********************************** Role Based Data ****************************************/
  let currentUser = localStorage.getItem("currentUser");
  if (currentUser === "admin") {
    roleData = accessLevel.adminRights;
    menuLinks = menuData.adminMenu;
    dashBoardLinks = dashBoardData.adminDashBoard;
    // role = "admin";
  } else if (currentUser === "approver") {
    roleData = accessLevel.approverRights;
    menuLinks = menuData.approverMenu;
    dashBoardLinks = dashBoardData.approverDashBoard;
    // role = "approver";
  } else if (currentUser === "sponsor") {
    roleData = accessLevel.sponsorRights;
    menuLinks = menuData.sponsorMenu;
    dashBoardLinks = dashBoardData.sponsorDashBoard;
    // role = "sponsor";
  } else {
    roleData = accessLevel.resourceRights;
    menuLinks = menuData.resourceMenu;
    dashBoardLinks = dashBoardData.resourceDashBoard;
    // role = "resource";
  }

  /*********************************** Drawer Handlers ****************************************/
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Root className="App">
          <CssBaseline />
          {userData?.email ? (
            <AppBarStyled
              position="fixed"
              style={{ paddingRight: "0px" }}
              open={open}
            >
              <CustomWidth>
                <MenuButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="center"
                  open={open}
                >
                  <MenuIcon style={{ width: "52px" }} />
                </MenuButton>
                <Header responseHandler={responseHandler} />
              </CustomWidth>
            </AppBarStyled>
          ) : null}

          {userData?.email ? (
            <DrawerStyled
              id="navigation-drawer"
              variant="permanent"
              open={open}
            >
              <ToolbarStyled>
                <IconButton onClick={handleDrawerClose}>
                  {theme.direction === "rtl" ? (
                    <ChevronRight />
                  ) : (
                    <ChevronLeft />
                  )}
                </IconButton>
              </ToolbarStyled>
              <SideMenu>
                {menuLinks.map((item) => (
                  <SideMenuItem key={item.id}>
                    <SideNavigate
                      name={item.name}
                      link={item.link}
                      icon={item.icon}
                      open={open}
                    />
                  </SideMenuItem>
                ))}
              </SideMenu>
              <Divider />
            </DrawerStyled>
          ) : null}

          <Content open={open} id="main">
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* General Routes */}
                <Route
                  path="/login"
                  element={<Login responseHandler={responseHandler} />}
                />
                <Route path="/google/auth" element={<GoogleAuth />} />
                <Route
                  path="/dashboard"
                  element={
                    <Dashboard
                      dashBoardLinks={dashBoardLinks}
                      dashBoardAccess={roleData.dashboard}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/unauthorized"
                  element={
                    <Unauthorized
                      dashBoardLinks={dashBoardLinks}
                      dashBoardAccess={roleData.dashboard}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route path="/404" element={<NotFound />} />
                <Route path="/time-out" element={<TimeOut />} />

                {/* Resource Routes */}
                <Route
                  path="/user/my-projects"
                  element={
                    <MyProjects
                      projectAccess={roleData.myProjects}
                      serviceAccess={roleData.services}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/user/projects"
                  element={
                    <UserProjects
                      projectAccess={roleData.projects}
                      serviceAccess={roleData.services}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/user/requests"
                  element={
                    <MyRequests
                      serviceAccess={roleData.services}
                      paymentAccess={roleData.payments}
                      responseHandler={responseHandler}
                    />
                  }
                />
                <Route
                  path="/user/pending-requests"
                  element={
                    <PendingRequests
                      serviceAccess={roleData.services}
                      paymentAccess={roleData.payments}
                      responseHandler={responseHandler}
                    />
                  }
                />

                <Route path="*" element={<Navigate to="/404" />} />
              </Routes>
            </Suspense>
            <Notification notify={notify} setNotify={setNotify} />
          </Content>
        </Root>
      </Router>
    </ThemeProvider>
  );
}
